/**
 * Share link payloads (decision 018), as URL-safe base64:
 *
 *   0x02  deflate(binary list incl. selections)
 *   0x03  varint n, deflate(binary list without selections) [n bytes], packed selections
 *
 * DEFLATE uses a preset dictionary. Inline selections compress well when most
 * kinks are unrated; packed ones (≈2.6 bits per choice with 6 ratings) win
 * for fully rated lists. The encoder emits whichever is shorter.
 *
 * The dictionary only helps compression, so any list encodes: the default
 * list's structure costs a few bytes, an edited list pays only for its
 * changes. Dictionaries are frozen per version; a new default list needs a
 * new version (the test pins the v1 dictionary's hash).
 */
import { deflateSync } from 'fflate';

import { DEFAULT_CATEGORIES, DEFAULT_RATINGS } from '../model/defaults';
import type { IdGen } from '../model/ids';
import { findInvariantViolations } from '../model/invariants';
import type { KinklistDoc } from '../model/types';
import {
  decodeBinary,
  encodeBinary,
  encodeStructure,
  packSelections,
  selectionDigits,
} from './binary';
import { base64UrlDecode, base64UrlEncode, concatBytes, inflateLimited } from './bytes';
import { ImportError } from './errors';

export const LINK_VERSION = 1;
const INLINE = (LINK_VERSION << 1) | 0;
const PACKED = (LINK_VERSION << 1) | 1;

let dictionaryV1: Uint8Array<ArrayBuffer> | null = null;

/** v1 dictionary: the binary structure of the v2 default list (SPEC Appendix A). */
export function linkDictionary(): Uint8Array<ArrayBuffer> {
  dictionaryV1 ??= encodeStructure(
    DEFAULT_RATINGS.map(([name, color]) => ({ name, color })),
    DEFAULT_CATEGORIES,
  );
  return dictionaryV1;
}

function varint(value: number): Uint8Array {
  const bytes: number[] = [];
  let n = value;
  do {
    bytes.push((n & 0x7f) | (n >= 0x80 ? 0x80 : 0));
    n >>>= 7;
  } while (n > 0);
  return Uint8Array.from(bytes);
}

function compress(data: Uint8Array): Uint8Array {
  return deflateSync(data, { level: 9, dictionary: linkDictionary() });
}

export function encodeLink(doc: KinklistDoc, options: { comments?: boolean } = {}): string {
  const inline = concatBytes([Uint8Array.of(INLINE), compress(encodeBinary(doc, options))]);
  const rest = compress(encodeBinary(doc, { ...options, selections: false }));
  const packed = concatBytes([
    Uint8Array.of(PACKED),
    varint(rest.length),
    rest,
    packSelections(selectionDigits(doc), doc.ratings.length),
  ]);
  return base64UrlEncode(packed.length < inline.length ? packed : inline);
}

/** Link payload → document with fresh ids. Throws `ImportError`. */
export function decodeLink(payload: string, ctx: { newId: IdGen; now: () => Date }): KinklistDoc {
  const bytes = base64UrlDecode(payload);
  let doc: KinklistDoc;
  if (bytes[0] === INLINE) {
    doc = decodeBinary(inflate(bytes.subarray(1)), ctx);
  } else if (bytes[0] === PACKED) {
    let length = 0;
    let at = 1;
    for (let shift = 0; ; shift += 7) {
      const byte = bytes[at++];
      if (byte === undefined || shift > 21) throw new ImportError('corrupt', 'Bad length');
      length |= (byte & 0x7f) << shift;
      if ((byte & 0x80) === 0) break;
    }
    if (at + length > bytes.length) throw new ImportError('corrupt', 'Truncated link');
    doc = decodeBinary(inflate(bytes.subarray(at, at + length)), ctx, bytes.subarray(at + length));
  } else {
    throw new ImportError('invalid', 'Unknown link version');
  }
  const problems = findInvariantViolations(doc);
  // (Should be unreachable: decodeBinary normalizes everything it reads.)
  if (problems.length > 0) throw new ImportError('invalid', problems.join('; '));
  return doc;
}

function inflate(data: Uint8Array): Uint8Array {
  return inflateLimited(data, 'raw', undefined, linkDictionary());
}
